import NiceModal from "@ebay/nice-modal-react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import SimpleModal from "components/modals/SimpleModal";
import { useFetchData, useGenericMutation } from "hooks/useAxios";
import { tokenState, userState } from "store/store";
import { TErrCodes, User } from "types/types";
import { DEFAULT_MESSAGE_ERROR } from "utils/const";

export const useLogin = () => {
  const setUser = useSetRecoilState(userState);
  const setToken = useSetRecoilState(tokenState);

  return useGenericMutation<
    { userId: string; password: string },
    User & { token: string }
  >({
    endpoint: "v1/auth/login",
    onSuccess: ({ token, ...user }) => {
      setUser(user);
      setToken(token);
    },
    invalidateQueries: [["auth/this"]],
    onError: (err) => {
      if (err?.response?.status === 400) {
        return;
      }
      NiceModal.show(SimpleModal, {
        title: "알림",
        message: err?.response?.data?.errMsg ?? DEFAULT_MESSAGE_ERROR,
        showCancel: false,
      });
    },
    meta: { errCode: TErrCodes.NONE },
  });
};

export const useLogout = () => {
  const setUser = useSetRecoilState(userState);
  const setToken = useSetRecoilState(tokenState);

  return useGenericMutation({
    endpoint: "v1/auth/logout",
    onSuccess: () => {
      setUser(null);
      setToken(null);
      localStorage.removeItem("lastActivity");
    },
    onError: () => {
      setUser(null);
      setToken(null);
      localStorage.removeItem("lastActivity");
    },
  });
};

export const useUpdateMyInfo = () =>
  useGenericMutation<{
    currentPassword?: string;
    password?: string;
    phone: string;
    bank: string;
    bankNumber: string;
  }>({
    endpoint: "v1/auth/update",
    invalidateQueries: [["auth/this"]],
    onSuccess: () => {
      NiceModal.show(SimpleModal, {
        title: "정보 수정",
        message: "정보가 수정되었습니다.",
        showCancel: false,
      });
    },
  });

export const useUser = () => {
  const [user, setUser] = useRecoilState(userState);
  const token = useRecoilValue(tokenState);

  return useFetchData<User>({
    endpoint: "v1/auth/this",
    queryKey: ["auth/this"],
    staleTime: 60 * 60 * 1000,
    enabled: !!token,
    placeholderData: user!,
    onSuccess: (data) => {
      setUser(data);
    },
    meta: {
      errCode: TErrCodes.NONE,
    },
  });
};

export const useGetMyInfo = () =>
  useFetchData<{
    noBonusCount: number | null;
    bonusCount: number | null;
    reportLossCount: number | null;
    reportTakenCount: number | null;
    noBonusSum: number | null;
    bonusSum: number | null;
    reportTakenSum: number | null;
  }>({
    endpoint: "v1/auth/info",
    queryKey: ["auth/info"],
  });
